/**
 * Polls `POST /api/steer` with the latest `SteerState` snapshot and holds the last
 * `SteerJudgment` + `DriftObservation`. Mirrors `client/focus/client.ts`'s shape
 * exactly: `fetchImpl`/`baseUrl`/`now` are injectable so `test/client/steer-client.test.ts`
 * can drive this against a fake fetch (and a fake clock); `poll` fire-and-forgets a
 * throttled POST and never throws into the caller's loop; on a network error (or a
 * 429/502) the last judgment/drift are kept as-is rather than cleared — `blendAssist`'s
 * own `maxAgeMs` (see `src/input/assist.ts`) is what ages an old judgment out, same
 * convention as the focus channel's `isFocused`.
 *
 * WIRE TYPES: `SteerState`/`SteerResponse` below are declared locally rather than
 * shared with the sibling server slice's `/api/steer` handler — deliberate duplication
 * per the task contract, so there's exactly one live writer per file. Keep this in
 * sync with the pinned wire contract by hand if it ever changes.
 *
 * CADENCE: adaptive, computed fresh from each `SteerState` passed to `poll` — see
 * `steerPollIntervalMs` below (exported standalone so `client/game.ts`'s HUD can show
 * the interval that would apply this frame without a live `SteerClient` instance).
 *
 * CLOCK: same clock-mismatch fix as `FocusClient` — the response's own `t` (server
 * wall-clock epoch) is discarded; the received judgment is stamped with `this.#now()`
 * (defaults to `performance.now`) at receipt, so it's comparable to the `nowMs` the
 * game loop later passes to `blendAssist`.
 */

import type { SteerJudgment } from "../../src";

export const TYPESAFE_STEER_UNCONFIGURED_BANNER = "TypeSafe not configured — steer assist offline";

/** Poll interval (ms) while the cursor is moving and close to a wall — overridable via
 * `?steerPoll=` (see `client/params.ts`). */
export const STEER_FAST_POLL_INTERVAL_MS_DEFAULT = 500;
/** Poll interval (ms) otherwise — fixed, not overridable. */
export const STEER_SLOW_POLL_INTERVAL_MS = 2000;
const FAST_SPEED_THRESHOLD_PX_S = 1;
const FAST_WALL_THRESHOLD_PX = 60;

/** Wire request body for `POST /api/steer` (see the task's pinned `/api/steer`
 * contract) — locally declared, see the module doc comment. */
export interface SteerState {
  gazeMeanDx: number;
  gazeMeanDy: number;
  gazeJitter: number;
  eyesOpen: boolean;
  blinkingNow: boolean;
  headMotionDegPerSecond: number;
  openLeft: boolean;
  openRight: boolean;
  openUp: boolean;
  openDown: boolean;
  directionToGoal: "left" | "right" | "up" | "down" | "none";
  distanceToNearestWallPx: number;
  cursorSpeedPxPerSecond: number;
  level: number;
  centrelineOffsetPx: number;
  recentWallSide: "left" | "right" | "up" | "down" | "none";
  windowMs: number;
}

export interface DriftObservation {
  direction: "none" | "left" | "right" | "up" | "down";
  confidence: number;
}

/** The `200` response shape for `/api/steer` (see the task's pinned contract). */
interface SteerResponseBody {
  intent: "left" | "right" | "up" | "down" | "hold";
  confidence: number;
  probabilities: Record<string, number>;
  reliable: number;
  drift: DriftObservation;
  t: number;
}

/**
 * Adaptive cadence: `fastMs` (default `STEER_FAST_POLL_INTERVAL_MS_DEFAULT`, 500) while
 * the cursor is moving (`cursorSpeedPxPerSecond > 1`) AND close to a wall
 * (`distanceToNearestWallPx < 60`); `STEER_SLOW_POLL_INTERVAL_MS` (2000) otherwise.
 */
export function steerPollIntervalMs(
  cursorSpeedPxPerSecond: number,
  distanceToNearestWallPx: number,
  fastMs: number = STEER_FAST_POLL_INTERVAL_MS_DEFAULT,
): number {
  return cursorSpeedPxPerSecond > FAST_SPEED_THRESHOLD_PX_S &&
    distanceToNearestWallPx < FAST_WALL_THRESHOLD_PX
    ? fastMs
    : STEER_SLOW_POLL_INTERVAL_MS;
}

export interface SteerClientOptions {
  fetchImpl?: typeof fetch;
  baseUrl?: string;
  /** Clock used to stamp a received judgment's `t`; defaults to `performance.now`. */
  now?: () => number;
  /** Overrides the fast-cadence interval (`?steerPoll=`, 200..5000ms). */
  fastPollIntervalMs?: number;
}

export class SteerClient {
  readonly #fetchImpl: typeof fetch;
  readonly #baseUrl: string;
  readonly #now: () => number;
  readonly #fastPollIntervalMs: number;
  #lastAttemptMs = -Infinity;
  #inFlight = false;
  #judgment: SteerJudgment | null = null;
  #drift: DriftObservation | null = null;
  #banner: string | null = null;

  constructor(opts: SteerClientOptions = {}) {
    this.#fetchImpl = opts.fetchImpl ?? fetch;
    this.#baseUrl = opts.baseUrl ?? "";
    this.#now = opts.now ?? (() => performance.now());
    this.#fastPollIntervalMs = opts.fastPollIntervalMs ?? STEER_FAST_POLL_INTERVAL_MS_DEFAULT;
  }

  getJudgment(): SteerJudgment | null {
    return this.#judgment;
  }

  getDrift(): DriftObservation | null {
    return this.#drift;
  }

  getBanner(): string | null {
    return this.#banner;
  }

  /** Call every frame; internally throttles to at most one in-flight POST per the
   * adaptive interval (see `steerPollIntervalMs`). */
  poll(nowMs: number, state: SteerState): void {
    if (this.#inFlight) return;
    const interval = steerPollIntervalMs(
      state.cursorSpeedPxPerSecond,
      state.distanceToNearestWallPx,
      this.#fastPollIntervalMs,
    );
    if (nowMs - this.#lastAttemptMs < interval) return;
    this.#lastAttemptMs = nowMs;
    this.#inFlight = true;
    void this.#send(state).finally(() => {
      this.#inFlight = false;
    });
  }

  async #send(state: SteerState): Promise<void> {
    try {
      const res = await this.#fetchImpl(`${this.#baseUrl}/api/steer`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(state),
      });
      if (res.status === 503) {
        this.#banner = TYPESAFE_STEER_UNCONFIGURED_BANNER;
        return;
      }
      if (!res.ok) {
        return; // 400/429/502 — keep the last judgment/drift, try again next poll.
      }
      const body = (await res.json()) as SteerResponseBody;
      // `body.t` is the server's own clock — not used here; see the module doc comment.
      this.#judgment = {
        intent: body.intent,
        confidence: body.confidence,
        reliable: body.reliable,
        t: this.#now(),
      };
      this.#drift = { direction: body.drift.direction, confidence: body.drift.confidence };
      this.#banner = null;
    } catch {
      // Network error — keep the last judgment/drift; blendAssist's maxAgeMs ages the
      // judgment out on its own.
    }
  }
}
